import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  CurrencyInput,
  MedicalCode,
  MedicalCodeMultiSelect,
  MedicalCodeTag,
} from '@eluve/blocks';
import {
  Button,
  Caption,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  Icon,
  Input,
  Separator,
  SimpleTooltip,
  Stack,
  Text,
  cn,
  useToast,
} from '@eluve/components';
import { useLatestSuperbill } from '@eluve/frontend-appointment-hooks';
import { ProceduralMedicalCodeTypes } from '@eluve/graphql-types';
import { useIsTenantAdminFromSession } from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';
import { billingCodeModifiersSchema } from '@eluve/utils';

import {
  AppointmentMedicalCode,
  useAppointmentBillingCodes,
} from './useAppointmentBillingCodes';
import { useGetFilePdfUrl } from './useGetFilePdfUrl';

// Private Components

interface ModifiersControlProps {
  medicalCode: AppointmentMedicalCode;
  onChange: (value: string[] | null) => void;
  modifiers: string[] | null;
}

const modifiersFormSchema = z.object({
  modifiers: billingCodeModifiersSchema.optional(),
});

type ModifiersForm = z.infer<typeof modifiersFormSchema>;

const ModifiersControl: React.FC<ModifiersControlProps> = ({
  medicalCode,
  onChange,
  modifiers,
}) => {
  const { isDialogOpen: isOpen, toggleDialog: toggle } = useDialog(false);

  const form = useForm<ModifiersForm>({
    defaultValues: { modifiers: (modifiers ?? []).join(',') },
    resolver: zodResolver(modifiersFormSchema),
  });

  useEffect(() => {
    form.setValue('modifiers', (modifiers ?? []).join(','));
  }, [form, modifiers]);

  const handleOpenChange = () => {
    toggle();
    form.reset({ modifiers: (modifiers ?? []).join(',') });
  };

  const onSubmit = async ({ modifiers }: ModifiersForm) => {
    onChange(modifiers && modifiers.length > 0 ? modifiers.split(',') : null);
    toggle();
  };

  const hasModifiers = modifiers && modifiers.length > 0;

  return (
    <Stack
      className={cn(
        'rounded-lg border border-border-primary p-1',
        hasModifiers && 'pl-4',
      )}
      gap={1}
      inline
      fullWidth={false}
      direction="horizontal"
    >
      {hasModifiers && (
        <div className="text-xs font-medium text-nowrap text-text-primary">
          {modifiers?.join(', ')}
        </div>
      )}
      <SimpleTooltip label="Edit modifiers">
        <Button variant="ghost" onClick={toggle}>
          <Icon name="Pencil" size="auto" />
        </Button>
      </SimpleTooltip>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <Form {...form}>
          <DialogContent size="sm" className="pt-9">
            <form
              className="flex flex-col gap-4"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <Stack align="center" gap={2}>
                <MedicalCodeTag
                  code={medicalCode.code}
                  codeType={medicalCode.codeType}
                />
                <Text className="text-center">{medicalCode.description}</Text>
              </Stack>
              <FormField
                control={form.control}
                name="modifiers"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription className="text-center">
                      A comma separated list of modifiers.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <DialogFooter className="sm:justify-center">
                <Button type="submit">Update Modifiers</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Form>
      </Dialog>
    </Stack>
  );
};

interface PriceControlProps {
  medicalCode: AppointmentMedicalCode;
  onChange: (value: number) => void;
  price: number;
}

const PriceControl: React.FC<PriceControlProps> = ({
  medicalCode,
  onChange,
  price,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | undefined>(
    ((price ?? 0) / 100).toFixed(2),
  );

  useEffect(() => {
    setValue(((price ?? 0) / 100).toFixed(2));
  }, [price]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    onChange(Math.floor(Number(value) * 100));
    setOpen(false);
    event.preventDefault();
  };

  return (
    <Stack
      className="rounded-lg border border-border-primary p-1 pl-4"
      gap={1}
      inline
      fullWidth={false}
      direction="horizontal"
    >
      <div className="text-xs font-medium text-text-primary">
        ${((price ?? 0) / 100).toFixed(2)}
      </div>
      <SimpleTooltip label="Edit price">
        <Button
          variant="ghost"
          disabled={price == null}
          onClick={() => {
            setOpen(true);
          }}
        >
          <Icon name="Pencil" size="auto" />
        </Button>
      </SimpleTooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="sm" className="pt-9">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <Stack align="center" gap={2}>
              <MedicalCodeTag
                code={medicalCode.code}
                codeType={medicalCode.codeType}
              />
              <Text className="text-center">{medicalCode.description}</Text>
            </Stack>
            <CurrencyInput onChange={setValue} value={value} />
            <DialogFooter className="sm:justify-center">
              <Button type="submit">Update Price</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

interface QuantityControlProps {
  max?: number;
  min?: number;
  onChange: (value: number) => void;
  value?: number;
}

const QuantityControl: React.FC<QuantityControlProps> = ({
  max,
  min = 0,
  onChange,
  value = 0,
}) => {
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleDecreaseClick = () => {
    const newValue = currentValue - 1;
    setCurrentValue(newValue);
    onChange(newValue);
  };

  const handleIncreaseClick = () => {
    const newValue = currentValue + 1;
    setCurrentValue(newValue);
    onChange(newValue);
  };

  return (
    <Stack
      className="rounded-lg border border-border-primary p-1"
      gap={1}
      inline
      fullWidth={false}
      direction="horizontal"
    >
      <Button
        variant="ghost"
        disabled={currentValue <= min}
        onClick={handleDecreaseClick}
      >
        <Icon name="MinusCircle" size="auto" />
      </Button>
      <div className="min-w-4 text-center text-xs font-medium text-text-primary select-none">
        {currentValue}
      </div>
      <Button
        variant="ghost"
        disabled={max != null && currentValue >= max}
        onClick={handleIncreaseClick}
      >
        <Icon name="PlusCircle" size="auto" />
      </Button>
    </Stack>
  );
};

// Public Components

const textClassName = 'text-xs font-medium text-text-primary';

export interface AppointmentBillingCodePricesProps {
  appointmentId: string;
  isReadonly?: boolean;
  tenantId: string;
}

export const AppointmentBillingCodePrices: React.FC<
  AppointmentBillingCodePricesProps
> = ({ appointmentId, isReadonly = false, tenantId }) => {
  const toast = useToast();
  const [isOpeningPdf, setIsOpeningPdf] = useState(false);
  const {
    billingCodes,
    getQuantity,
    updateLink,
    updateLinks,
    updateModifiers,
    updatePrice,
    updateQuantity,
  } = useAppointmentBillingCodes({ appointmentId, tenantId });
  const { refetch: refetchSuperbill, superbill } = useLatestSuperbill();
  const handlePdf = useGetFilePdfUrl({
    appointmentId,
    tenantId,
    type: 'SUPERBILL',
  });

  const openPdf = async () => {
    const newTab = window.open('about:blank', '_blank');
    setIsOpeningPdf(true);
    const response = await handlePdf();
    if (response.status === 201 && newTab) {
      newTab.location = response.body.pdfUrl;
      refetchSuperbill();
    } else {
      toast.error('Failed to prepare PDF');
    }
    setIsOpeningPdf(false);
  };

  const isEditingAdminOnly = useIsFeatureFlagEnabled(
    'BILLING_CODE_EDITING_IS_ADMIN_ONLY',
  );
  const isTenantAdmin = useIsTenantAdminFromSession();
  const canEditBillingCodes = !isEditingAdminOnly || isTenantAdmin;

  const cptCodes = billingCodes.filter(
    (code) =>
      code.medical_code?.codeType &&
      ProceduralMedicalCodeTypes.includes(code.medical_code?.codeType),
  );
  const icdCodes = billingCodes.filter(
    (code) => code.medical_code?.codeType === 'ICD_10',
  );

  if (cptCodes.length === 0) {
    return null;
  }

  return (
    <Stack gap={3}>
      <Caption color="tertiary" className="text-xs">
        Billing Details
      </Caption>
      {cptCodes.map(
        (cptCode) =>
          cptCode.medical_code && (
            <div
              key={cptCode.id}
              className="w-full rounded-lg border border-border-primary bg-white p-2"
            >
              <Stack
                align="center"
                className="md:flex-row"
                gap={2}
                justify="between"
              >
                <Stack
                  className="grow"
                  inline
                  justify="between"
                  direction="horizontal"
                >
                  <MedicalCode
                    code={cptCode.medical_code.code}
                    codeType={cptCode.medical_code.codeType}
                    description={cptCode?.description ?? ''}
                    type="ghost"
                  />
                  {isReadonly || !canEditBillingCodes ? (
                    <div className={cn(textClassName, 'mr-2')}>
                      {(cptCode.modifiers ?? []).join(', ')}
                    </div>
                  ) : (
                    <ModifiersControl
                      medicalCode={cptCode.medical_code}
                      modifiers={(cptCode.modifiers as string[]) ?? []}
                      onChange={(value) => {
                        updateModifiers(cptCode.id, value);
                      }}
                    />
                  )}
                </Stack>
                <div className="h-px w-full shrink-0 bg-border-primary md:h-8 md:w-px" />
                <Stack
                  className="self-start sm:w-auto sm:gap-2 md:justify-around md:self-center"
                  gap={1}
                  inline
                  justify="between"
                  direction="horizontal"
                >
                  {isReadonly ? (
                    <div className={cn(textClassName, 'ml-2')}>
                      {getQuantity(cptCode)}
                    </div>
                  ) : (
                    <QuantityControl
                      max={50}
                      min={1}
                      onChange={(value) => {
                        updateQuantity(cptCode.id, value);
                      }}
                      value={getQuantity(cptCode)}
                    />
                  )}
                  <Icon name="X" className="text-text-disabled" />
                  {isReadonly || !canEditBillingCodes ? (
                    <div className={textClassName}>
                      {`$${((cptCode.price ?? 0) / 100).toFixed(2)}`}
                    </div>
                  ) : (
                    <PriceControl
                      medicalCode={cptCode.medical_code}
                      onChange={(value) => {
                        updatePrice(cptCode.id, value);
                      }}
                      price={cptCode.price ?? 0}
                    />
                  )}
                  <Icon name="Equal" className="text-text-disabled" />
                  <div className={cn(textClassName, 'pr-1.5 sm:pr-3.5')}>
                    {`$${((getQuantity(cptCode) * (cptCode.price ?? 0)) / 100).toFixed(2)}`}
                  </div>
                </Stack>
              </Stack>
              <Separator className="my-2" />
              {icdCodes.length ? (
                <Stack
                  className="sm:flex-row sm:flex-wrap"
                  gap={2}
                  align="stretch"
                >
                  {!isReadonly && (
                    <MedicalCodeMultiSelect
                      selectedCount={cptCode.linked_codes.length}
                      totalCount={icdCodes.length}
                      onClick={() =>
                        updateLinks(
                          cptCode.id,
                          cptCode.linked_codes.map(
                            (link) => link.targetAppointmentBillingCodeId,
                          ),
                          icdCodes.map((icdCode) => icdCode.id),
                        )
                      }
                    />
                  )}
                  {icdCodes.map((icdCode) => {
                    const isSelected = cptCode.linked_codes.some(
                      (link) =>
                        link.targetAppointmentBillingCodeId === icdCode.id,
                    );

                    if (isReadonly && !isSelected) return null;

                    if (!icdCode.medical_code) return null;

                    return (
                      <MedicalCode
                        key={icdCode.id}
                        code={icdCode.medical_code.code}
                        codeType={icdCode.medical_code.codeType}
                        description={
                          icdCode?.description ??
                          icdCode.medical_code.description ??
                          ''
                        }
                        onClick={
                          isReadonly
                            ? undefined
                            : () =>
                                updateLink(isSelected, cptCode.id, icdCode.id)
                        }
                        startAdornment={
                          isReadonly ? null : (
                            <Checkbox checked={isSelected} className="ml-1" />
                          )
                        }
                        type={isSelected ? 'outline' : 'outlineDashed'}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Stack align="center" className="p-3" gap={2}>
                  <Text color="tertiary" className="text-center">
                    No ICD codes added above yet.
                  </Text>
                </Stack>
              )}
            </div>
          ),
      )}
      {!isReadonly && (
        <Stack justify="end" gap={2} direction="horizontal">
          <Button
            variant="outline"
            loading={isOpeningPdf}
            onClick={openPdf}
          >{`${superbill ? 'Update' : 'Generate'} Superbill`}</Button>
        </Stack>
      )}
    </Stack>
  );
};
